/**
 * Press Release Detail View - EXACT COPY of ReportDetailView pattern
 */
import React, { useState } from 'react';
import { useToast, ToastContainer } from '../common/Toast';
import pressReleaseService from '../../services/pressReleaseService';

const PressReleaseDetailView = ({ pressRelease, onBack, onUpdate }) => {
  const { toasts, showToast, removeToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentPressRelease, setCurrentPressRelease] = useState(pressRelease);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: pressRelease.title || '',
    description: pressRelease.description || '',
    company: pressRelease.company || '',
    notes: pressRelease.notes || ''
  });

  const handleScrape = async () => {
    try {
      setLoading(true);
      showToast('Scraping press release content...', 'info');
      
      const response = await pressReleaseService.scrapePressRelease(currentPressRelease.id);
      
      if (response && response.success) {
        showToast('✅ Content scraped successfully!', 'success');
        
        const updatedPressRelease = {
          ...currentPressRelease,
          content_extracted: true,
          content_text: response.data?.content_preview || '',
          status: 'completed'
        };
        setCurrentPressRelease(updatedPressRelease);
        
        if (onUpdate) {
          onUpdate(updatedPressRelease);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Scraping failed';
        showToast(`❌ Scraping failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error scraping content:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Scraping error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateArticle = async () => {
    try {
      setLoading(true);
      showToast('Generating article from press release...', 'info');
      
      const response = await pressReleaseService.generateArticle(currentPressRelease.id);
      
      if (response && response.success) {
        showToast('✅ Article generated successfully!', 'success');
        
        const updatedPressRelease = {
          ...currentPressRelease,
          article_drafted: true,
          ai_article_draft: response.data?.article_content || ''
        };
        setCurrentPressRelease(updatedPressRelease);
        
        if (onUpdate) {
          onUpdate(updatedPressRelease);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Article generation failed';
        showToast(`❌ Article generation failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error generating article:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Article generation error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this press release? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      showToast('Deleting press release...', 'info');
      
      const response = await pressReleaseService.deletePressRelease(currentPressRelease.id);
      
      if (response && response.success) {
        showToast('✅ Press release deleted successfully!', 'success');
        setTimeout(() => {
          onBack(); // Return to list view
        }, 1000);
      } else {
        const errorMsg = response?.error || response?.message || 'Delete failed';
        showToast(`❌ Delete failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting press release:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Delete error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      showToast('Saving changes...', 'info');
      
      const response = await pressReleaseService.updatePressRelease(currentPressRelease.id, editData);
      
      if (response && response.success) {
        showToast('✅ Press release updated successfully!', 'success');
        
        const updatedPressRelease = { ...currentPressRelease, ...editData };
        setCurrentPressRelease(updatedPressRelease);
        setIsEditing(false);
        
        if (onUpdate) {
          onUpdate(updatedPressRelease);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Update failed';
        showToast(`❌ Update failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error updating press release:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Update error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      title: currentPressRelease.title || '',
      description: currentPressRelease.description || '',
      company: currentPressRelease.company || '',
      notes: currentPressRelease.notes || ''
    });
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header - EXACT SAME AS REPORTS */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{currentPressRelease.title}</h1>
              <p className="text-sm text-gray-600">
                {currentPressRelease.company} • {formatDate(currentPressRelease.publication_date)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!isEditing && (
              <>
                {!currentPressRelease.content_extracted && (
                  <button
                    onClick={handleScrape}
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                        <span>Scraping...</span>
                      </>
                    ) : (
                      <span>Scrape Now</span>
                    )}
                  </button>
                )}
                
                {currentPressRelease.content_extracted && (
                  <button
                    onClick={handleGenerateArticle}
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <span>{currentPressRelease.article_drafted ? 'Regenerate Article' : 'Generate Article'}</span>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Edit
                </button>
                
                <button
                  onClick={handleDelete}
                  className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
            
            {isEditing && (
              <>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content - EXACT SAME LAYOUT AS REPORTS */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={editData.title}
                        onChange={(e) => setEditData({...editData, title: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editData.description}
                        onChange={(e) => setEditData({...editData, description: e.target.value})}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={editData.company}
                        onChange={(e) => setEditData({...editData, company: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({...editData, notes: e.target.value})}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Title:</span>
                      <p className="text-gray-900 mt-1">{currentPressRelease.title}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Company:</span>
                      <p className="text-gray-900 mt-1">{currentPressRelease.company}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Publication Date:</span>
                      <p className="text-gray-900 mt-1">{formatDate(currentPressRelease.publication_date)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">RSS Source:</span>
                      <p className="text-gray-900 mt-1">{currentPressRelease.rss_source_name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <p className="text-gray-900 mt-1 capitalize">{currentPressRelease.status}</p>
                    </div>
                    
                    {currentPressRelease.press_release_url && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Original URL:</span>
                        <p className="text-gray-900 mt-1">
                          <a href={currentPressRelease.press_release_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            {currentPressRelease.press_release_url}
                          </a>
                        </p>
                      </div>
                    )}
                    
                    {currentPressRelease.description && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Description:</span>
                        <p className="text-gray-900 text-sm">{currentPressRelease.description}</p>
                      </div>
                    )}
                    
                    {currentPressRelease.notes && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Notes:</span>
                        <p className="text-gray-900 text-sm">{currentPressRelease.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Extracted Content */}
              {currentPressRelease.content_extracted && (
                <div className="space-y-6">
                  {/* Cleaned Content (Primary) */}
                  {currentPressRelease.cleaned_content_text && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        📝 Cleaned Press Release Content
                        <span className="ml-2 text-sm font-normal text-green-600">AI Processed</span>
                      </h2>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                          {currentPressRelease.cleaned_content_text}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Cleaned content • {currentPressRelease.cleaned_content_text.length} characters
                      </div>
                    </div>
                  )}
                  
                  {/* Raw Content (Collapsible) */}
                  {currentPressRelease.content_text && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <details className="group">
                        <summary className="cursor-pointer text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between hover:text-gray-700">
                          🔍 Raw Scraped Content
                          <span className="text-sm font-normal text-gray-500 group-open:hidden">Click to expand</span>
                          <span className="text-sm font-normal text-gray-500 hidden group-open:inline">Click to collapse</span>
                        </summary>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                            {currentPressRelease.content_text}
                          </pre>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Raw scraped content • {currentPressRelease.content_text.length} characters
                        </div>
                      </details>
                    </div>
                  )}
                  
                  {/* Fallback to raw content only if no cleaned content */}
                  {!currentPressRelease.cleaned_content_text && currentPressRelease.content_text && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        📄 Extracted Content
                        <span className="ml-2 text-sm font-normal text-yellow-600">Raw Scraped</span>
                      </h2>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                          {currentPressRelease.content_text}
                        </pre>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Raw content • {currentPressRelease.content_text.length} characters
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Generated Article */}
              {currentPressRelease.article_drafted && currentPressRelease.ai_article_draft && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Generated Article</h2>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                      {currentPressRelease.ai_article_draft}
                    </pre>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Article generated • {currentPressRelease.ai_article_draft.length} characters
                  </div>
                </div>
              )}

              {/* No Content State */}
              {!currentPressRelease.content_extracted && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mb-4 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                    <h3 className="text-lg font-medium mb-2 text-gray-900">Content Not Extracted</h3>
                    <p className="text-gray-600 mb-4">Click "Scrape Now" to extract text content from this press release.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - EXACT SAME AS REPORTS */}
            <div className="space-y-6">
              
              {/* Processing Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Content Extracted</span>
                    {currentPressRelease.content_extracted ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Article Generated</span>
                    {currentPressRelease.article_drafted ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">WordPress Published</span>
                    {currentPressRelease.wordpress_pushed ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default PressReleaseDetailView;